import { Injectable } from '@angular/core';
import { PortalFieldConfig, uppercaseParser } from '@portal/form/data-access';
import { distinctUntilChanged, map, startWith, tap } from 'rxjs';
import { CommonReportingForEntityModelKeys, CrsTaxResidenceKeys } from '../const/form-field-key.const';
import { FormGroup, Validators } from '@angular/forms';

@Injectable({ providedIn: 'root' })
export class CrsForEntityFormConfigService {
  initCrsForEntityFields(): PortalFieldConfig[] {
    return [
      {
        template:
          "<p class='mb-3'>Common Reporting Standard (CRS) requires company to indicate the type of entity and the Tax Residence with TIN.</p>",
      },
      {
        key: CommonReportingForEntityModelKeys.typeOfFinancial,
        type: '#typeOfFinancial',
        props: { label: 'Types of Entity', required: true },
      },

      {
        fieldGroup: [
          {
            key: CommonReportingForEntityModelKeys.taxResidences,
            type: 'form-array-crs-for-entity',
            props: { required: true },
            expressions: {
              'props.required': field => {
                const form = field?.formControl?.parent as FormGroup;
                const doNotHaveOneControl = form?.get('doNotHaveOne');
                return !doNotHaveOneControl?.value;
              },
            },
            hooks: {
              onInit: field => {
                const { formControl, parent } = field;
                const doNotHaveOneField: PortalFieldConfig | undefined = parent?.get?.(CommonReportingForEntityModelKeys.doNotHaveOne);

                if (!formControl) return;

                return formControl?.valueChanges.pipe(
                  startWith(formControl?.value),
                  map(value => (Array.isArray(value) ? value.length : null)),
                  distinctUntilChanged(),
                  tap((value: number | null) => {
                    if (value === null) return;

                    if (value > 0) {
                      doNotHaveOneField?.formControl?.removeValidators(Validators.requiredTrue);
                      doNotHaveOneField?.formControl?.updateValueAndValidity();
                      doNotHaveOneField?.formControl?.setValue(false);
                      return;
                    }

                    if (value === 0) {
                      doNotHaveOneField?.formControl?.setValidators(Validators.requiredTrue);
                      doNotHaveOneField?.formControl?.updateValueAndValidity();
                      doNotHaveOneField?.formControl?.setValue(true);
                    }
                  }),
                );
              },
            },
            fieldArray: {
              fieldGroupClassName: 'field-columns',
              fieldGroup: [
                {
                  key: CrsTaxResidenceKeys.countryId,
                  type: '#country',
                  props: { label: 'Country of Tax Residence', required: true },
                },
                {
                  key: CrsTaxResidenceKeys.tin,
                  type: 'input',
                  validators: { validation: ['tin'] },
                  parsers: [uppercaseParser],
                  props: {
                    label: 'Tax Identification Number (TIN)',
                    placeholder: 'e.g. 1234567A',
                    required: true,
                    maskConfig: { mask: 'A*' },
                  },
                },
              ],
            },
          },
          {
            key: CommonReportingForEntityModelKeys.doNotHaveOne,
            type: 'checkbox-boolean',
            props: { labelCheckbox: 'I do not have one' },
            hooks: {
              onInit: field => {
                const { formControl, parent } = field;

                if (!formControl) return;

                const taxResidencesField: PortalFieldConfig | undefined = parent?.get?.(CommonReportingForEntityModelKeys.taxResidences);

                /**
                 * Reproduce steps:
                 * 1. Input Tax Residences and Go to Step 7
                 * 2. Click Edit button
                 * 3. Check the checkbox "I do not have one"
                 * 4. Click Cancel button
                 * 5. Click Edit button
                 * 6. Check the checkbox "I do not have one"
                 * 7. Bug here => Cannot read properties of undefined (reading 'formControl')
                 * 8. Click Cancel button
                 * 9. Click Edit button
                 * 10. Check the checkbox "I do not have one"
                 * 11. Bug here => Tax Residences does not get removed
                 *
                 *
                 * NOTE: Fixed
                 */

                return formControl?.valueChanges.pipe(
                  startWith(formControl?.value),
                  distinctUntilChanged(),
                  tap(value => {
                    const taxResidences = field.model?.[CommonReportingForEntityModelKeys.taxResidences];
                    if (value && Array.isArray(taxResidences)) {
                      while (taxResidences.length) {
                        taxResidencesField?.props?.['remove']?.(0);
                      }
                      taxResidencesField?.formControl?.updateValueAndValidity();
                    }
                  }),
                );
              },
            },
          },
        ],
      },
    ];
  }
}
